import { useContext } from 'react';

import { useAppDispatch } from '@/hooks';
import { useAppParams } from '@/navigation';
import { FormContext } from '@/forms/contexts';
import { FormDomain } from '@/forms/typings';
import { AttestationApi, MissingItemApi, SectionApi } from '@/api';
import { fetchAttestationSummary, fetchSectionSummary } from '@/store/thunks';
import type { Result } from '@/types/result.interface';
import type { AppError } from '@/types/errors.interface';
import type { ProgressEvent } from '@/services/http';

type UploadFileParams = {
  itemId: string;
  file: File;
  onUploadProgress?: (progressEvent: ProgressEvent) => void;
  cancelToken?: AbortSignal;
};

type DeleteFileParams = {
  itemId: string;
  realName: string;
};

/**
 * Returns upload/delete file callbacks bound to the current FormDomain.
 * InputFile calls these without knowing which domain it lives in — the hook picks
 * the right API and reads the right URL params (applicationId+sectionId for AppForm,
 * applicationId alone for Attestation, missingItemId for MPI).
 */
export const useFileActions = () => {
  const dispatch = useAppDispatch();
  const { domain } = useContext(FormContext);
  const { applicationId, sectionId, missingItemId } = useAppParams();

  const appId = Number(applicationId);
  const sectId = Number(sectionId);
  const mpiId = Number(missingItemId);
  const isMPI = domain === FormDomain.MissingItemForm;
  const isAttestation = domain === FormDomain.Attestation;

  const uploadFile = (
    params: UploadFileParams,
  ): Promise<Result<string, AppError>> => {
    if (isMPI) {
      return new MissingItemApi().uploadFile({
        missingItemFormId: mpiId,
        ...params,
      });
    }

    if (isAttestation) {
      return new AttestationApi().uploadFile({
        applicationId: appId,
        ...params,
      });
    }

    return new SectionApi().uploadFile({
      applicationId: appId,
      sectionId: sectId,
      ...params,
    });
  };

  const deleteFile = (
    params: DeleteFileParams,
  ): Promise<Result<boolean, AppError>> => {
    if (isMPI) {
      return new MissingItemApi().deleteFile({
        missingItemFormId: mpiId,
        ...params,
      });
    }

    if (isAttestation) {
      return new AttestationApi().deleteFile({
        applicationId: appId,
        ...params,
      });
    }

    return new SectionApi().deleteFile({
      applicationId: appId,
      sectionId: sectId,
      ...params,
    });
  };

  /**
   * Re-reads the progress counters after a file landed or was removed. The upload and
   * delete endpoints answer with the stored file name alone, so nothing else tells the
   * sidebar and header that a required document is now satisfied.
   */
  const refreshSummary = () => {
    // MPI counters live on the dashboard, not on the form page, and no summary
    // endpoint backs them — the recomputed figure arrives with the next dashboard load.
    if (isMPI) return;

    if (isAttestation) {
      dispatch(fetchAttestationSummary(appId));
      return;
    }

    dispatch(fetchSectionSummary(appId, sectId));
  };

  return { uploadFile, deleteFile, refreshSummary };
};
